import React, { useState, useEffect } from 'react';
import { Button } from 'antd';
import s from './s.module.less';
import AIChat from 'components/AIChat';
import PPAOnboardingHeader from 'components/PPAOnboardingHeader';
import CelebrationSection from './components/CelebrationSection';
import SignUpForm from './components/SignUpForm';
// import type { SignUpFormData } from './types';
import { EAIChatPageId } from 'types/ai';
import { TLeadFormStepData } from 'api/ppaLeadForm';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'constants/path';
import cx from 'classnames';
import { trackHeapEvent } from 'utils/analytics';
import { getUrlParam } from 'utils/common';
import { getUserToken, setLocalStoreProviderTokenSetTime } from 'utils/localstore';
import { setCookieProviderToken, setCookieProviderTokenSetTime } from 'utils/shareAccount';
import { getCurrentUser } from 'api/common';
import { ERoleCode } from 'types/enumItems';
import { getIntentRedirect } from '../PPALeadFormPage/getIntentRedirect';
import { isCredentialingHelpType } from '../PPALeadFormPage/helpType';

interface PPASignUpPageProps {
    leadFormData?: TLeadFormStepData;
    onChangeEmail?: () => void;
    emailDisabled?: boolean;
}

export default function PPASignUpPage({ leadFormData, onChangeEmail, emailDisabled }: PPASignUpPageProps) {
    const [step, setStep] = useState(1);
    const [isMobile, setIsMobile] = useState(false);
    const navigate = useNavigate();
    const [isChatVisible, setIsChatVisible] = useState(false);
    const urlAction = getUrlParam('action') || '';
    const isCredentialing = isCredentialingHelpType(leadFormData?.practiceHelpType);

    // 检测是否为移动端
    useEffect(() => {
        const checkIsMobile = () => {
            setIsMobile(window.innerWidth <= 992);
        };

        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);

        return () => {
            window.removeEventListener('resize', checkIsMobile);
        };
    }, []);

    const handleFormSubmit = async () => {
        try {
            const token = getUserToken();
            if (token) {
                setLocalStoreProviderTokenSetTime(new Date().getTime());
                setCookieProviderToken(token);
                setCookieProviderTokenSetTime(new Date().getTime());
            }
        } catch (error) {
            console.error(error);
        }

        try {
            const userRes = await getCurrentUser();
            const roleCode = userRes?.data?.data?.roleCode;
            if (
                roleCode === ERoleCode.GROUP_ADMINISTRATIVE_ASSISTANT ||
                roleCode === ERoleCode.GROUP_CLINICAL_ASSISTANT
            ) {
                navigate(`${PATH.DASHBOARD}/clients`);
                return;
            }
        } catch (error) {
            console.error(error);
        }

        const practiceHelpType = leadFormData?.practiceHelpType;
        if (practiceHelpType) {
            try {
                const target = await getIntentRedirect(practiceHelpType);
                navigate(target);
                return;
            } catch {
                // fallthrough
            }
        }

        navigate(PATH.PPA_ONBOARDING_STEPS);
    };

    const handleNext = async () => {
        if (step < 2) {
            // 移动端下一步埋点
            trackHeapEvent('WebApp - PPA Sign Up - Click - Mobile Next', {
                fromStep: step,
                toStep: step + 1,
            });
            setStep(step + 1);
        }
    };

    const handleChangeEmail = () => {
        onChangeEmail?.();
    };

    const renderMobileStep = () => {
        switch (step) {
            case 1:
                return <CelebrationSection leadFormData={leadFormData} />;
            case 2:
                return (
                    <SignUpForm
                        initialValues={{
                            email: leadFormData?.email || '',
                            agreeToTerms: true,
                            subscribeToNewsletter: true,
                        }}
                        onSubmit={handleFormSubmit}
                        onChangeEmail={handleChangeEmail}
                        emailDisabled={urlAction !== 'signup'}
                        isCredentialing={isCredentialing}
                    />
                );
            default:
                return <CelebrationSection leadFormData={leadFormData} />;
        }
    };

    // PC端的渲染
    const renderDesktopContent = () => {
        return (
            <>
                <CelebrationSection leadFormData={leadFormData} />
                <SignUpForm
                    initialValues={{
                        email: leadFormData?.email || '',
                        agreeToTerms: true,
                        subscribeToNewsletter: true,
                    }}
                    onSubmit={handleFormSubmit}
                    onChangeEmail={handleChangeEmail}
                    emailDisabled={urlAction !== 'signup'}
                />
            </>
        );
    };

    const handleChatVisibleChange = (show: boolean) => {
        // AIChat 可见性变化埋点
        trackHeapEvent('WebApp - PPA Sign Up - View - AIChat Visibility Change', {
            isVisible: show,
            isMobile,
            step,
        });
        setIsChatVisible(show);
    };

    return (
        <div className={s.wrap}>
            <PPAOnboardingHeader />
            <div className={s.content}>
                <div className={s.contentWrap}>
                    <div className={s.contentIn}>{isMobile ? renderMobileStep() : renderDesktopContent()}</div>
                    <div className={cx(s.chatWrap, isChatVisible && s.chatWrapVisible)}>
                        <AIChat
                            defaultShow={false}
                            pageId={EAIChatPageId.PROVIDER_ONBOARDING_SIGN_UP}
                            onVisableChange={handleChatVisibleChange}
                        />
                    </div>
                </div>

                {/* 移动端导航按钮 */}
                {isMobile && step < 2 && (
                    <div className={s.buttonsContainer}>
                        <Button type="primary" size="large" onClick={handleNext} className={s.navButton}>
                            Next
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
